/* eslint-disable jest/require-hook */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import autoBind from 'react-autobind';

import { ComparisonTable } from '../../comparison-table';
import { isWiseQuote } from '../../resources/quoteFilters';
import styles from '../../resources/static/less/comparison-components.module.less';
import { Table } from '../../resources/tracking/types';

import { AmountToSend, MidMarketRate, RouteSelector } from './atoms';

function createClassNames(classNames) {
  return classNames.map((className) => `${styles[className]}`).join(' ');
}

class Comparison extends Component {
  static mapQuoteToMidMarketRate(twQuote) {
    return {
      rate: twQuote.rate,
      source: twQuote.source,
      target: twQuote.target,
      time: twQuote.dateCollected,
    };
  }

  constructor(props) {
    super(props);
    autoBind(this);
    this.state = {
      amount: props.amount, // FIXME: Updates to these props aren't propagated to the state
      sourceCurrency: props.sourceCurrency,
      targetCurrency: props.targetCurrency,
    };
  }

  onRouteChange(route) {
    const { onRouteChange } = this.props;
    const context = { ...this.props, ...this.state };
    onRouteChange(route, context);
    this.setState({ ...route });
  }

  onAmountChangeFinish(amount) {
    const { onAmountChangeFinish } = this.props;
    const context = { ...this.props, ...this.state };
    onAmountChangeFinish(amount, context);
  }

  getMidMarketRate() {
    const { midMarketRate, quotes } = this.props;
    const twQuote = quotes.find((quote) => isWiseQuote(quote));
    return twQuote ? Comparison.mapQuoteToMidMarketRate(twQuote) : midMarketRate;
  }

  render() {
    const {
      amount,
      quotes,
      loadingRates,
      loadingQuotes,
      sourceCountry,
      providerCountry,
      providers,
      maxVisibleProviders,
      expandDisclaimer,
      excludePartners,
      error,
      errorType,
      onAmountChange,
      onAmountChangeFinish,
      onRouteChange,
      onError,
      onCallToActionClick,
      onClickToSeePricingDisclaimer,
      onClickToSeeDisclaimer,
      onProviderClick,
      deliveryEstimationEnabled,
      onClickingDeliveryEstimationTooltip,
      showSendMoneyButton,
      providerLogoRedirectEnabled,
      trackEvent,
      showMoreProvidersOption,
      page,
    } = this.props;

    const { sourceCurrency, targetCurrency } = this.state;

    return (
      <div className={styles['comparison-widget']}>
        <div className={styles.card}>
          <div className={styles['card-header']}>
            <RouteSelector
              sourceCurrency={sourceCurrency}
              targetCurrency={targetCurrency}
              onRouteChange={this.onRouteChange}
            />
            <div
              className={createClassNames([
                'd-flex',
                'flex-column',
                'flex-row-md',
                'align-items-center',
              ])}
            >
              <MidMarketRate
                source={sourceCurrency}
                target={targetCurrency}
                midMarketRate={!loadingRates && !loadingQuotes && this.getMidMarketRate()} // TODO maybe to state
                loading={loadingRates || loadingQuotes}
              />

              <div className={styles['m-x-md-4']} />
              <AmountToSend
                placeholderAmount={1000}
                amount={amount}
                onChange={onAmountChange}
                onChangeFinish={this.onAmountChangeFinish}
                source={sourceCurrency}
              />
            </div>
          </div>
        </div>

        <ComparisonTable
          amount={amount}
          sourceCurrency={sourceCurrency}
          targetCurrency={targetCurrency}
          midMarketRate={!loadingRates && !loadingQuotes && this.getMidMarketRate()} // TODO maybe to state
          quotes={quotes}
          loadingRates={loadingRates}
          loadingQuotes={loadingQuotes}
          sourceCountry={sourceCountry}
          providerCountry={providerCountry}
          providers={providers}
          maxVisibleProviders={maxVisibleProviders}
          expandDisclaimer={expandDisclaimer}
          excludePartners={excludePartners}
          error={error}
          errorType={errorType}
          onAmountChange={onAmountChange}
          onAmountChangeFinish={onAmountChangeFinish}
          onRouteChange={onRouteChange}
          onError={onError}
          onCallToActionClick={onCallToActionClick}
          onClickToSeePricingDisclaimer={onClickToSeePricingDisclaimer}
          onClickToSeeDisclaimer={onClickToSeeDisclaimer}
          deliveryEstimationEnabled={deliveryEstimationEnabled}
          onClickingDeliveryEstimationTooltip={onClickingDeliveryEstimationTooltip}
          showSendMoneyButton={showSendMoneyButton}
          providerLogoRedirectEnabled={providerLogoRedirectEnabled}
          onProviderClick={onProviderClick}
          trackEvent={trackEvent}
          showMoreProvidersOption={showMoreProvidersOption}
          page={page}
        />
      </div>
    );
  }
}

// TODO Disabling linting for now as the passed props themselves need a refactor
/* eslint-disable react/require-default-props */
Comparison.propTypes = {
  amount: PropTypes.number.isRequired,
  sourceCurrency: PropTypes.string.isRequired,
  targetCurrency: PropTypes.string.isRequired,

  // eslint-disable-next-line react/require-default-props
  midMarketRate: PropTypes.shape({
    rate: PropTypes.number.isRequired,
    source: PropTypes.string.isRequired,
    target: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
  }), // Probably should be required, but as could be async today w/ WithComparisonResources, its optional

  quotes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      alias: PropTypes.string.isRequired,
      logo: PropTypes.string.isRequired,
      rate: PropTypes.number.isRequired,
      fees: PropTypes.number.isRequired,
      receivedAmount: PropTypes.number.isRequired,
      dateCollected: PropTypes.string.isRequired,
    }),
  ), // Probably should be required, but as could be async today w/ WithComparisonResources, its optional

  loadingRates: PropTypes.bool.isRequired,
  loadingQuotes: PropTypes.bool.isRequired,

  sourceCountry: PropTypes.string,
  providerCountry: PropTypes.string,

  providers: PropTypes.arrayOf(PropTypes.string),
  maxVisibleProviders: PropTypes.number,
  expandDisclaimer: PropTypes.bool,

  excludePartners: PropTypes.bool,

  error: PropTypes.bool,
  errorType: PropTypes.oneOf([null, 'UNSUPPORTED_ROUTE', 'EXCEPTION']),

  onAmountChange: PropTypes.func,
  onAmountChangeFinish: PropTypes.func,
  onRouteChange: PropTypes.func,
  onError: PropTypes.func,
  onCallToActionClick: PropTypes.func,
  onClickToSeePricingDisclaimer: PropTypes.func,
  onClickToSeeDisclaimer: PropTypes.func,
  onProviderClick: PropTypes.func,
  onClickingDeliveryEstimationTooltip: PropTypes.func,
  deliveryEstimationEnabled: PropTypes.bool,
  showSendMoneyButton: PropTypes.bool,
  providerLogoRedirectEnabled: PropTypes.bool,
  trackEvent: PropTypes.func,
  showMoreProvidersOption: PropTypes.bool,
  page: PropTypes.string,
};

Comparison.defaultProps = {
  sourceCountry: null,
  providerCountry: null,
  providers: [],
  excludePartners: false,
  expandDisclaimer: false,
  maxVisibleProviders: 5,

  error: false,
  errorType: null,

  onAmountChange: () => {},
  onAmountChangeFinish: () => {},
  onRouteChange: () => {},
  onError: () => {},
  onCallToActionClick: () => {},
  onClickToSeePricingDisclaimer: () => {},
  onClickToSeeDisclaimer: () => {},
  onProviderClick: () => {},
  onClickingDeliveryEstimationTooltip: () => {},
  deliveryEstimationEnabled: false,
  showSendMoneyButton: true,
  providerLogoRedirectEnabled: false,
  trackEvent: () => {},
  showMoreProvidersOption: true,
  page: null,
};

// eslint-disable-next-line fp/no-mutation
Comparison.componentType = Table.Educational;

export default Comparison;
